/**
 * web-account-pages D3 — the message banner's queue (the app's client banner,
 * in-app-notifications/client-notification-banner.html). Pure.
 *
 * A message from the studio that arrives while the panel is closed raises one banner: a secure
 * message, a Telegram reply or a notice, never the client's own. One shows at a time for six
 * seconds; at most three are held (the one showing and two waiting), the rest are dropped. A
 * message id raises a banner once: offered, shown, dropped or cleared, it never comes back.
 */
import type { StreamItem } from './messageStream';

export const BANNER_MS = 6_000;
/** The one showing and those waiting behind it. */
export const BANNER_QUEUE_CAP = 3;

export interface Banner {
  /** The message's id: the panel opens at it. */
  readonly id: string;
  /** The start of the message, in one line. */
  readonly preview: string;
}

export interface BannerQueue {
  readonly showing: Banner | null;
  readonly waiting: readonly Banner[];
  /** Every message id ever offered. */
  readonly offered: ReadonlySet<string>;
}

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", apos: "'", nbsp: ' ' };

/** Display-safe notice markup as plain words: tags become spaces, the common entities their characters. */
function plainText(html: string): string {
  return html.replace(/<[^>]*>/g, ' ').replace(/&(amp|lt|gt|quot|#39|apos|nbsp);/g, (_, name: string) => ENTITIES[name]);
}

const oneLine = (text: string) => text.replace(/\s+/g, ' ').trim();

/** The banner a stream item raises, or null: only what the studio sent. */
export function bannerOf({ entry }: StreamItem): Banner | null {
  if (entry.direction !== 'outbound') return null;
  switch (entry.kind) {
    case 'chat-message':
    case 'reply':
      return { id: entry.id, preview: oneLine(entry.payload.text) };
    case 'automatic':
      return { id: entry.id, preview: oneLine(entry.payload.text === null ? entry.payload.label : plainText(entry.payload.text)) };
    default:
      return null;
  }
}

export function emptyBanners(): BannerQueue {
  return { showing: null, waiting: [], offered: new Set() };
}

export function offerBanner(queue: BannerQueue, banner: Banner): BannerQueue {
  if (queue.offered.has(banner.id)) return queue;
  const offered = new Set(queue.offered).add(banner.id);
  if (!queue.showing) return { showing: banner, waiting: queue.waiting, offered };
  if (1 + queue.waiting.length >= BANNER_QUEUE_CAP) return { ...queue, offered };
  return { showing: queue.showing, waiting: [...queue.waiting, banner], offered };
}

/** The showing banner went (its six seconds, or a click): the next waiting one shows. */
export function advanceBanners(queue: BannerQueue): BannerQueue {
  const [next = null, ...rest] = queue.waiting;
  return { showing: next, waiting: rest, offered: queue.offered };
}

/** The panel opened: nothing shows or waits; what was offered stays offered. */
export function clearBanners(queue: BannerQueue): BannerQueue {
  return { showing: null, waiting: [], offered: queue.offered };
}
